/*
  PINGO STUDIOS — Floating Header Controller
  DS v4 Phase 3, sub-unit 3.1. Spec: docs/DESIGN-SYSTEM-REDESIGN.md §1.7.

  Replaces Horizon's native sticky layer (disabled via enable_sticky_header:
  "never" in sections/header-group.json) with a floating island controlled by
  the adopted frontend stack (docs/FRONTEND-STACK.md):

    • Alpine  = UI state   — the discrete `mode` ('top' | 'revealed' | 'hidden')
                             and the data attributes / native solid styling it drives.
    • GSAP    = motion      — the translateY follow + hide/show tween.
    • ScrollTrigger = sensor — reports scroll position + direction each update.

  Behaviour (unified, desktop + mobile):
    • At the very top      → header sits below the announcement bar and scrolls
                             with the page (mode 'top'); transparent over the hero
                             on transparent templates (native G5 colours kept).
    • Scroll down mid-page → hidden (slides up off-screen).
    • Scroll up   mid-page → revealed in the regular (solid) global scheme + a
                             discreet shadow, pinned to the top of the viewport.
    • Transparent is a top-of-page-only state.

  Loaded `defer` AFTER pingo-animations.js and BEFORE alpine.min.js (document
  order) in both layouts, so it registers its Alpine component on `alpine:init`.

  The fixed positioning + spacer are gated on `.pingo-header-ready` (added once
  measured) so there is no flash of a top:0 header before init; without JS the
  header stays in native flow. prefers-reduced-motion → state changes apply
  instantly (no tweens).
*/
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var HIDE_CLEARANCE = 24; // extra px so the discreet shadow clears the viewport when hidden
  var COMMIT_THRESHOLD = 10; // px of SUSTAINED movement before flipping hide/reveal — kills
                             // momentum/trackpad jitter (tiny direction flips don't toggle)
  var TOP_HYSTERESIS = 12; // dead-zone around the announcement boundary so crossing it
                           // doesn't rapid-toggle transparent↔solid (BUG 3 flicker)

  function registerHeader() {
    if (!window.Alpine) return;
    if (window.gsap && window.ScrollTrigger) gsap.registerPlugin(ScrollTrigger);

    Alpine.data('pingoHeader', function () {
      return {
        section: null, // the <header class="header-section"> wrapper (the element we move)
        spacer: null, // reserves the header's flow space once it goes position:fixed
        annH: 0, // summed height of the header-group children above the header (announcement bar)
        headH: 0, // header section height
        hideOnScroll: true, // merchant setting — when false, header stays visible (never hides)
        revealShadow: true, // merchant setting — discreet shadow on the revealed (solid) state

        // ─── State: a scroll-derived `mode` + the `menuOpen` overlay ───────────
        mode: 'top', // 'top' | 'revealed' | 'hidden' (scroll only)
        menuOpen: false, // a mega-menu panel is open (hover/focus on a submenu trigger)
        lastY: 0,
        acc: 0,
        _rafPending: false,
        _lastKey: null, // last applied render key — guards against re-firing the tween every frame

        init: function () {
          var self = this;
          this.section = this.$el.closest('.header-section') || this.$el.parentElement;
          if (!this.section) return;

          this.hideOnScroll = this.$el.dataset.pingoHideOnScroll !== 'false';
          this.revealShadow = this.$el.dataset.pingoRevealShadow !== 'false';
          if (this.revealShadow) this.section.classList.add('pingo-header--shadow');

          this.measure();

          this.spacer = document.createElement('div');
          this.spacer.className = 'pingo-header-spacer';
          this.section.insertAdjacentElement('afterend', this.spacer);
          this.applySpacer();

          this.setY(this.annH);
          document.documentElement.classList.add('pingo-header-ready');

          // Scroll SENSOR — viewport-aware. The scroll CONTAINER differs by breakpoint
          // (Horizon base.css): at ≥990px the page scrolls inside .page-wrapper (html/
          // body locked) and ScrollTrigger's default scroller — pinned to .page-wrapper
          // in pingo-animations.js — watches it; BELOW 990px the page scrolls the
          // WINDOW, which that ScrollTrigger never sees. So on mobile onScroll never
          // fired and the header never hid. Read scroll from the ACTUAL scroller per
          // viewport: ScrollTrigger (desktop) + a window listener (mobile / no-ST
          // fallback), each guarded so the two never both drive it.
          var desktopMedia = window.matchMedia('(min-width: 990px)');
          var pageWrapper = document.querySelector('.page-wrapper');
          // Exposed for onScroll's rubber-band clamp (needs the active scroller).
          self._desktopMedia = desktopMedia;
          self._pageWrapper = pageWrapper;
          function scrollPos() {
            return desktopMedia.matches && pageWrapper ? pageWrapper.scrollTop || 0 : window.scrollY || 0;
          }
          if (window.ScrollTrigger) {
            ScrollTrigger.create({
              onUpdate: function (s) { if (desktopMedia.matches) self.onScroll(s.scroll()); },
              onRefresh: function () { self.measure(); self.applySpacer(); },
            });
          }
          // Window scroll = the mobile scroller (and the no-ScrollTrigger fallback).
          // Guarded to <990px when ScrollTrigger is present so the two sensors don't
          // fight; on desktop the window is overflow:hidden, so it would never fire.
          window.addEventListener('scroll', function () {
            if (!window.ScrollTrigger || !desktopMedia.matches) self.onScroll(window.scrollY || 0);
          }, { passive: true });

          // WIDTH-only guard. iOS/Android collapse the URL bar on the first scroll,
          // firing a HEIGHT-only resize; re-measuring there (offsetHeight reads =
          // forced reflow) mid-scroll contributes to the "frozen first scroll".
          // Header geometry depends only on width, so skip height-only resizes.
          var lastHeaderVW = window.innerWidth;
          window.addEventListener('resize', function () {
            if (window.innerWidth === lastHeaderVW) return;
            lastHeaderVW = window.innerWidth;
            self.measure();
            self.applySpacer();
          }, { passive: true });

          // Breakpoint crossing swaps the scroller → re-measure and re-sync the mode
          // from the now-active scroller's position (else the header keeps a stale
          // hidden/revealed state until the next scroll).
          desktopMedia.addEventListener('change', function () {
            self.measure();
            self.applySpacer();
            self.lastY = scrollPos();
            self.acc = 0;
            self.onScroll(scrollPos());
          });

          // Mega-menu open = any submenu-trigger item hovered/focused. Re-evaluated
          // on a rAF so :hover/:focus-within has settled.
          var reeval = function () {
            if (self._rafPending) return;
            self._rafPending = true;
            requestAnimationFrame(function () { self._rafPending = false; self.setMenuOpen(self.detectMenuOpen()); });
          };
          // Detect on genuine pointer MOVEMENT (+ boundary exit + keyboard), NOT
          // pointerover/out. over/out fire every frame when the follow tween micro-
          // moves the fixed header under a STATIONARY pointer during a top-zone scroll
          // → a :has/:hover querySelector each frame (the subtle scroll-up stutter) and
          // spurious opens. pointermove only fires on real movement, so a stationary
          // hover-and-scroll runs zero detection.
          this.$el.addEventListener('pointermove', reeval, { passive: true });
          this.$el.addEventListener('pointerleave', reeval, { passive: true });
          this.$el.addEventListener('focusin', reeval);
          this.$el.addEventListener('focusout', reeval);

          this.applyState();
        },

        isTransparentTemplate: function () { return this.$el.hasAttribute('transparent'); },

        measure: function () {
          this.headH = this.section.offsetHeight || 0;
          var a = 0, n = this.section.previousElementSibling;
          while (n) { a += n.offsetHeight || 0; n = n.previousElementSibling; }
          this.annH = a;
        },

        applySpacer: function () {
          // Owner directive (2026-06-18): the floating header overlays the first
          // section on EVERY page — no reserved gap anywhere. Transparent templates
          // were already 0; regular templates now match (the at-top look goes solid
          // in applyState so the bar stays legible over real content).
          if (this.spacer) this.spacer.style.height = '0px';
        },

        setY: function (px) {
          // Direct transform write. The hide/reveal ANIMATION is a COMPOSITOR CSS
          // transition on the section (pingo-custom.css), gated by data-header-state,
          // so motion never runs on the main-thread rAF (no stop-motion under scroll/
          // touch contention). The top-zone follow uses the same write at 0s.
          this.section.style.transform = 'translateY(' + px + 'px)';
          // Live Y for descendants that are `position:fixed` (menu drawer). A
          // transform on this section makes it their containing block, so they
          // must counter this offset to stay viewport-sized. See pingo-custom.css.
          this.section.style.setProperty('--pingo-header-y', px + 'px');
        },

        detectMenuOpen: function () {
          // 4.1.4 moved aria-haspopup off the submenu trigger <a> onto a sibling
          // disclosure <button> (and only the overflow "Mais" item still has it
          // on the link). Match the panel itself so hover at the top still flips
          // the island solid — `:has([aria-haspopup])` silently no-oped after the merge.
          return !!this.$el.querySelector('.menu-list__list-item:is(:hover, :focus-within):has(.menu-list__submenu, [aria-haspopup])');
        },
        setMenuOpen: function (v) { if (this.menuOpen !== v) { this.menuOpen = v; this.applyState(); } },

        onScroll: function (y) {
          // iOS rubber-band guard (2026-07-22): overscroll reports positions
          // OUTSIDE the scroller's real range (negative at the top bounce,
          // > max at the bottom bounce), and the bounce-back registers as an
          // opposite-direction scroll — ≥10px of it committed a reveal, so the
          // header flapped in over the footer on every bottom bounce (and the
          // top bounce pushed the follow past its resting point via negative y).
          // Clamp to the true range so overscroll reads as motionless. The
          // scrollHeight/clientHeight reads don't invalidate layout (no forced
          // reflow — invariant-safe).
          var max = this._desktopMedia && this._desktopMedia.matches && this._pageWrapper
            ? this._pageWrapper.scrollHeight - this._pageWrapper.clientHeight
            : document.documentElement.scrollHeight - window.innerHeight;
          if (max > 0) {
            if (y < 0) y = 0;
            else if (y > max) y = max;
          }
          // 'top' zone, with LEAVE-side hysteresis: once at the top, stay 'top'
          // (transparent) until scrolled clearly past the announcement (annH + HYST)
          // — a dead-zone so crossing the boundary can't rapid-toggle. Entering 'top'
          // is at annH exactly, so the follow position stays continuous (no jump).
          var topBoundary = this.mode === 'top' ? this.annH + TOP_HYSTERESIS : this.annH;
          if (y <= topBoundary) {
            if (this.mode !== 'top') { this.mode = 'top'; this.acc = 0; }
            // Freeze the follow while a menu is open — the island must not creep under
            // the pointer mid-hover (motion + pointer churn), and the open panel stays
            // anchored to a stationary island.
            if (!this.menuOpen) this.setY(this.annH - Math.min(y, this.annH));
            this.lastY = y;
          } else {
            // Below the fold: direction-COMMIT hide/reveal (1B accumulator). Leaving
            // 'top' by scrolling DOWN never passes through 'revealed' — it stays 'top'
            // (transparent) until the hide commits, so it hides in one motion with no
            // transparent→solid flash. Tiny jitter (< threshold) never toggles.
            var delta = y - this.lastY;
            this.lastY = y;
            if (delta !== 0) {
              if ((delta > 0) !== (this.acc > 0)) this.acc = 0; // reversal → restart count
              this.acc += delta;
              if (this.acc >= COMMIT_THRESHOLD) { this.mode = this.hideOnScroll ? 'hidden' : 'revealed'; }
              else if (this.acc <= -COMMIT_THRESHOLD) { this.mode = 'revealed'; }
            }
          }
          this.applyState();
        },

        // ─── SINGLE SOURCE OF TRUTH — fixed priority, top to bottom ────────────
        //   1. menu open   → ALWAYS solid + visible (even at the very top).
        //   2. hidden      → off-screen (slide up, keep current look → no flash).
        //   3. mode 'top'  → transparent.
        //   4. otherwise   → solid (revealed).
        applyState: function () {
          // IDEMPOTENT: derive a render KEY; only act when it changes. applyState runs
          // every scroll frame, so without this the hide/reveal tween re-fires every
          // frame (gsap.to + overwrite restarts the ease ~14×/transition = stutter).
          // The 'top' follow position is a cheap gsap.set in onScroll (not a tween),
          // so it stays smooth without re-running this.
          var key = this.menuOpen ? 'menu' : this.mode;
          if (key === this._lastKey) return;
          this._lastKey = key;

          // NB: set data-header-state BEFORE setY — the attr selects the CSS
          // transition (0.32s for hidden/solid), so it must be live when the
          // transform changes for the compositor transition to run.
          if (this.menuOpen) {
            this.$el.dataset.stickyState = 'active';
            this.section.dataset.headerState = 'solid';
            if (this.mode !== 'top') this.setY(0); // pinned; at top stay at the follow position
            return;
          }
          if (this.mode === 'top') {
            // Keep header-state 'transparent' = the 0s transform transition, so the
            // announcement-follow stays instant (perf invariant #1) on EVERY page.
            // The bar BACKGROUND is the only difference: transparent templates stay
            // see-through; regular templates go SOLID (sticky-state) since the header
            // now floats OVER the first section (spacer 0) and a see-through bar over
            // real content would be illegible.
            this.section.dataset.headerState = 'transparent';
            if (this.isTransparentTemplate()) {
              this.$el.removeAttribute('data-sticky-state');
            } else {
              this.$el.dataset.stickyState = 'active';
            }
            // position via setY (follow) in onScroll
          } else if (this.mode === 'hidden') {
            this.section.dataset.headerState = 'hidden';
            this.setY(-(this.headH + HIDE_CLEARANCE)); // don't touch sticky-state → no flash
          } else {
            this.section.dataset.headerState = 'solid';
            this.$el.dataset.stickyState = 'active';
            this.setY(0);
          }
        },
      };
    });
  }

  document.addEventListener('alpine:init', registerHeader);
}());
